import type {ModalContainerProps, ModalDialogProps} from "@project/design-system-web";
import type {ComponentProps} from "react";

import {Modal, cn, useMediaQuery} from "@project/design-system-web";

import {useOverlayPortalContainer} from "../overlay-portal-context";

function AppModalContainer({
  className,
  placement,
  scroll = "inside",
  ...props
}: ModalContainerProps) {
  const isDesktop = useMediaQuery("(min-width: 640px)");
  const resolvedPlacement = placement ?? (isDesktop ? "center" : "top");

  return (
    <Modal.Container
      className={cn("p-1! sm:p-10!", className)}
      placement={resolvedPlacement}
      scroll={scroll}
      {...props}
    />
  );
}

function AppModalDialog({className, ...props}: ModalDialogProps) {
  return (
    <Modal.Dialog
      className={cn(
        "flex max-h-[calc(100%-0.25rem)] w-[calc(100%-0.25rem)] flex-col overflow-hidden sm:max-w-md sm:data-[placement=center]:translate-y-[-5vh]",
        className,
      )}
      {...props}
    />
  );
}

function AppModalHeader({className, ...props}: ComponentProps<typeof Modal.Header>) {
  return <Modal.Header className={cn("shrink-0", className)} {...props} />;
}

function AppModalBody({className, ...props}: ComponentProps<typeof Modal.Body>) {
  return (
    <Modal.Body className={cn("min-h-0 flex-1 overflow-y-auto p-4 sm:p-6", className)} {...props} />
  );
}

function AppModalFooter({className, ...props}: ComponentProps<typeof Modal.Footer>) {
  return <Modal.Footer className={cn("shrink-0", className)} {...props} />;
}

function AppModalBackdrop({
  UNSTABLE_portalContainer,
  ...props
}: ComponentProps<typeof Modal.Backdrop>) {
  const scopedPortalContainer = useOverlayPortalContainer();

  // HeroUI overlay portal must be explicit here. React Aria context can split across package instances.
  return (
    <Modal.Backdrop
      UNSTABLE_portalContainer={UNSTABLE_portalContainer ?? scopedPortalContainer ?? undefined}
      {...props}
    />
  );
}

export const AppModal = {
  Root: Modal,
  Backdrop: AppModalBackdrop,
  Body: AppModalBody,
  CloseTrigger: Modal.CloseTrigger,
  Container: AppModalContainer,
  Dialog: AppModalDialog,
  Footer: AppModalFooter,
  Header: AppModalHeader,
  Heading: Modal.Heading,
  Icon: Modal.Icon,
  Trigger: Modal.Trigger,
};
